import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  fetchClientSites,
  fetchSiteById,
  fetchSiteLookup,
  createSite,
  updateSite,
  deleteSite,
  activateSite,
  deactivateSite,
} from "../api/siteApi"
import type { CreateSiteRequest, UpdateSiteRequest } from "./types"
import axios from "axios"
import { toast } from "sonner"
import { getApiErrorMessage, getApiErrorStatus } from "@/shared/api/getApiErrorMessage"
import { explainBillingRefusal } from "@/shared/api/billingToast"
import { useLookup } from "@/shared/hooks/useLookup"
import { lookupKeys } from "@/shared/queries/lookupKeys"
import { invalidateLookups } from "@/shared/queries/invalidateLookups"
import { billingKeys } from "@/entities/billing/model/keys"

/**
 * Query hook to fetch sites for a client
 */
export const useClientSites = (clientId: number) => {
  return useQuery({
    queryKey: ['client-sites', clientId],
    queryFn: () => fetchClientSites(clientId),
    enabled: !!clientId,
  })
}

/**
 * Query hook to fetch sites for a single client (picker).
 */
export const useSiteLookup = (clientId?: number) =>
  useLookup({
    queryKey: lookupKeys.site.byClient(clientId),
    queryFn: () => fetchSiteLookup(clientId),
    tier: 'standard',
    enabled: !!clientId,
  })

/**
 * Query hook to fetch every site in the current account, regardless of client.
 */
export const useAllSitesLookup = (enabled = true) =>
  useLookup({
    queryKey: lookupKeys.site.all,
    queryFn: () => fetchSiteLookup(),
    tier: 'standard',
    enabled,
  })

/**
 * Query hook to fetch sites by specific IDs.
 */
export const useSiteLookupByIds = (ids: number[]) =>
  useLookup({
    queryKey: lookupKeys.site.byIds(ids),
    queryFn: () => fetchSiteLookup(undefined, ids),
    tier: 'standard',
    enabled: ids.length > 0,
  })

export const useSite = (siteId: number) => {
  return useQuery({
    queryKey: ['site', siteId],
    queryFn: () => fetchSiteById(siteId),
    enabled: !!siteId,
  })
}

/**
 * Mutation hook to create a new site
 */
export const useCreateSite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clientId, request }: { clientId: number; request: CreateSiteRequest }) =>
      createSite(clientId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-sites', variables.clientId] })
      invalidateLookups(queryClient, 'site')
      queryClient.invalidateQueries({ queryKey: billingKeys.all })
    },
  })
}

/**
 * Mutation hook to update a site
 */
export const useUpdateSite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ siteId, request }: { siteId: number; request: UpdateSiteRequest }) =>
      updateSite(siteId, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['client-sites', data.clientId] })
      queryClient.invalidateQueries({ queryKey: ['site', data.siteId] })
      invalidateLookups(queryClient, 'site')
    },
  })
}

/**
 * Mutation hook to delete a site
 */
export const useDeleteSite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (siteId: number) => deleteSite(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-sites'] })
      invalidateLookups(queryClient, 'site')
    },
  })
}

/**
 * Mutation hook to switch a parked site back on. The 402 refusal is toasted by the API client with the
 * plan's numbers and a link to the billing page, so callers need no error handling of their own.
 */
export const useActivateSite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (site: { siteId: number; clientId: number }) => activateSite(site.siteId),
    onSuccess: (_, site) => {
      queryClient.invalidateQueries({ queryKey: ['client-sites', site.clientId] })
      queryClient.invalidateQueries({ queryKey: ['site', site.siteId] })
      invalidateLookups(queryClient, 'site')
      queryClient.invalidateQueries({ queryKey: billingKeys.all })
    },
  })
}

const UNFINISHED_SHIFTS_FALLBACK =
  "A site can only be deactivated once every shift at it has ended, including one in progress. " +
  "Cancel or move the remaining shifts, or wait until they have finished, then try again."

/**
 * Mutation hook to park an active site. Its default error toast is skipped (see `deactivateSite`), so
 * every refusal is explained here: a 409 says the site still has unfinished shifts, a 404 says it can no
 * longer be seen, and a billing refusal reads the way the API client would have said it.
 */
export const useDeactivateSite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (site: { siteId: number; clientId: number }) => deactivateSite(site.siteId),
    onSuccess: (_, site) => {
      queryClient.invalidateQueries({ queryKey: ['client-sites', site.clientId] })
      queryClient.invalidateQueries({ queryKey: ['site', site.siteId] })
      invalidateLookups(queryClient, 'site')
      queryClient.invalidateQueries({ queryKey: billingKeys.all })
    },
    onError: async (error, site) => {
      if (explainBillingRefusal(error)) return

      const status = getApiErrorStatus(error)
      if (status === 409) {
        // The backend says how many shifts and what to do about them; fall back to the general rule
        // if the body is ever not the usual { message }.
        const body: unknown = axios.isAxiosError(error) ? error.response?.data : undefined
        const detail =
          body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string'
            ? (body as { message: string }).message
            : UNFINISHED_SHIFTS_FALLBACK
        toast.error("This site still has shifts that haven't finished", {
          description: detail,
          duration: 10000,
        })
        return
      }

      if (status === 404) {
        // Gone, or no longer visible to this user: refresh the list so the screen stops offering it.
        queryClient.invalidateQueries({ queryKey: ['client-sites', site.clientId] })
        toast.error('This site could not be found. It may have been removed, or you may no longer have access to it.')
        return
      }

      toast.error(await getApiErrorMessage(error))
    },
  })
}
